import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { usePhotoUpload } from "@/hooks/useUpload";
import { Camera, Save, Plus, X, Trash2, Sparkles, ArrowLeft, RefreshCw, Star } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import InterestsButton from "@/components/profile/InterestsButton";
import InterestSelectorModal from "@/components/profile/InterestSelectorModal";
import NotificationBell from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import ProfileVideoSection from "@/components/profile/ProfileVideoSection";
import ProfileVideosManager from "@/components/profile/ProfileVideosManager";
import BottomNav from "@/components/BottomNav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type GenderType = Database["public"]["Enums"]["gender_type"];

interface PhotoItem {
  id: string;
  storage_path: string;
  url: string;
  is_primary?: boolean;
  position?: number;
}

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, profile, refreshProfile } = useAuthContext();
  const navigate = useNavigate();
  const { uploadProfilePhoto, uploadPhoto, deletePhoto, uploading } = usePhotoUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [name, setName] = useState("");
  const [age, setAge] = useState<number | "">("");
  const [bio, setBio] = useState("");
  const [gender, setGender] = useState<GenderType | "">("");
  const [location, setLocation] = useState("");
  const [religion, setReligion] = useState("");
  const [annualIncome, setAnnualIncome] = useState("");
  const [profession, setProfession] = useState("");
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [education, setEducation] = useState("");
  const [socioeconomicStatus, setSocioeconomicStatus] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [willingToRelocate, setWillingToRelocate] = useState("");
  const [children, setChildren] = useState("");
  const [showInterests, setShowInterests] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<PhotoItem | null>(null);
  const [replacingPhotoId, setReplacingPhotoId] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setAge(profile.age || "");
      setBio(profile.bio || "");
      setGender(profile.gender || "");
      setLocation(profile.location || "");
      setReligion(profile.religion || "");
      // @ts-ignore - new fields may not be in types yet
      setAnnualIncome(profile.annual_income || "");
      // @ts-ignore
      setProfession(profile.profession || "");
      // @ts-ignore
      setCountry(profile.country || "");
      // @ts-ignore
      setNationality(profile.nationality || "");
      // @ts-ignore
      setMaritalStatus(profile.marital_status || "");
      // @ts-ignore
      setEducation(profile.education || "");
      // @ts-ignore
      setSocioeconomicStatus(profile.socioeconomic_status || "");
      // @ts-ignore
      setLookingFor(profile.looking_for || "");
      // @ts-ignore
      setWillingToRelocate(profile.willing_to_relocate || "");
      // @ts-ignore
      setChildren(profile.children || "");
    }
  }, [profile]);

  // Fetch user photos - reusable function
  const fetchPhotos = useCallback(async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("profile_photos")
      .select("id, storage_path, is_primary, position")
      .eq("user_id", user.id)
      .order("position", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });

    if (!error && data) {
      const photosWithUrls = data.map((photo) => ({
        ...photo,
        url: supabase.storage.from("profile-photos").getPublicUrl(photo.storage_path).data.publicUrl,
      }));
      // Sort: primary first, then by position, then by created_at
      photosWithUrls.sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        if (a.position !== null && b.position !== null) return a.position - b.position;
        if (a.position !== null) return -1;
        if (b.position !== null) return 1;
        return 0;
      });
      setPhotos(photosWithUrls);
    }
  }, [user]);

  // Fetch user photos on mount and when user changes
  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleProfilePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await uploadProfilePhoto(file);
      if (url) {
        refreshProfile();
        toast({ title: "Profile photo updated!" });
      }
    }
  };

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) {
      e.target.value = ""; // Reset input
      return;
    }
    
    if (photos.length >= 6) {
      toast({ title: "Maximum 6 photos allowed", variant: "destructive" });
      e.target.value = ""; // Reset input
      return;
    }

    const url = await uploadPhoto(file);
    if (!url) {
      toast({ title: "Failed to upload photo", variant: "destructive" });
      e.target.value = ""; // Reset input
      return;
    }

    // Wait a bit for the database insert to complete
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Fetch all photos to get the newly added one
    const { data: allPhotos } = await supabase
      .from("profile_photos")
      .select("id, storage_path, is_primary, position")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (allPhotos && allPhotos.length > 0) {
      // Find the photo we just uploaded (should be the most recent)
      const newPhoto = allPhotos[0];
      const position = photos.length;
      const isPrimary = photos.length === 0;
      
      // Update the newly added photo with position and primary status
      await supabase
        .from("profile_photos")
        .update({ position, is_primary: isPrimary })
        .eq("id", newPhoto.id);
      
      if (isPrimary) {
        await supabase
          .from("profiles")
          .update({ photo_url: url })
          .eq("id", user.id);
        refreshProfile();
      }
    }
    
    // Refetch all photos to update the UI
    await fetchPhotos();
    toast({ title: "Photo added!" });
    e.target.value = ""; // Reset input so same file can be selected again
  };

  const handleDeletePhoto = async (photo: PhotoItem) => {
    const { error } = await deletePhoto(photo.storage_path);
    if (!error) {
      // If deleted photo was primary, make first remaining photo primary
      if (photo.is_primary && photos.length > 1) {
        const nextPhoto = photos.find(p => p.id !== photo.id);
        if (nextPhoto) {
          await supabase
            .from("profile_photos")
            .update({ is_primary: true })
            .eq("id", nextPhoto.id);
          
          // Update profile photo_url if needed
          if (nextPhoto.url) {
            await supabase
              .from("profiles")
              .update({ photo_url: nextPhoto.url })
              .eq("id", user?.id);
            refreshProfile();
          }
        }
      }
      
      // Refetch photos to update UI
      await fetchPhotos();
      toast({ title: "Photo deleted" });
      setShowDeleteConfirm(null);
    } else {
      toast({ title: "Failed to delete photo", variant: "destructive" });
    }
  };

  const handleReplacePhoto = async (photoId: string, file: File) => {
    if (!user) return;
    
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    setReplacingPhotoId(photoId);
    
    // Delete old photo from storage
    const { error: deleteError } = await supabase.storage
      .from("profile-photos")
      .remove([photo.storage_path]);
    
    if (deleteError) {
      console.error("Error deleting old photo:", deleteError);
    }
    
    // Upload new photo
    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-photos")
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      setReplacingPhotoId(null);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("profile-photos")
      .getPublicUrl(fileName);

    // Update the photo record
    const { error: dbError } = await supabase
      .from("profile_photos")
      .update({ storage_path: fileName })
      .eq("id", photoId);

    if (dbError) {
      toast({ title: "Failed to update photo", variant: "destructive" });
      setReplacingPhotoId(null);
      return;
    }

    // If this was the primary photo, update profile photo_url
    if (photo.is_primary) {
      await supabase
        .from("profiles")
        .update({ photo_url: publicUrl })
        .eq("id", user.id);
      refreshProfile();
    }
    
    // Refetch photos to update UI
    await fetchPhotos();
    toast({ title: "Photo replaced!" });
    setReplacingPhotoId(null);
  };

  const handleSetMainPhoto = async (photoId: string) => {
    if (!user) return;
    
    // Remove primary from all photos
    await supabase
      .from("profile_photos")
      .update({ is_primary: false })
      .eq("user_id", user.id);
    
    // Set new primary
    const { error } = await supabase
      .from("profile_photos")
      .update({ is_primary: true })
      .eq("id", photoId);
    
    if (!error) {
      // Update profile photo_url
      const photo = photos.find(p => p.id === photoId);
      if (photo) {
        await supabase
          .from("profiles")
          .update({ photo_url: photo.url })
          .eq("id", user.id);
        refreshProfile();
      }
      
      // Refetch photos to update UI
      await fetchPhotos();
      toast({ title: "Main photo updated!" });
    }
  };

  const handleSave = async () => {
    if (!user) return;
    if (!name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }

    setLoading(true);
    const profileData = {
      name: name.trim(),
      age: age ? Number(age) : null,
      bio: bio.trim() || null,
      gender: gender || null,
      location: location.trim() || null,
      religion: religion.trim() || null,
      annual_income: annualIncome || null,
      profession: profession.trim() || null,
      country: country.trim() || null,
      nationality: nationality.trim() || null,
      marital_status: maritalStatus || null,
      education: education.trim() || null,
      socioeconomic_status: socioeconomicStatus || null,
      looking_for: lookingFor || null,
      willing_to_relocate: willingToRelocate || null,
      children: children || null,
    };

    let error;
    if (profile) {
      ({ error } = await supabase.from("profiles").update(profileData).eq("id", user.id));
    } else {
      ({ error } = await supabase.from("profiles").insert({ id: user.id, ...profileData }));
    }

    setLoading(false);
    if (error) {
      toast({ title: "Error saving profile", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profile saved!" });
      refreshProfile();
    }
  };

  return (
    <div className="min-h-screen sky-gradient pb-24">
      <header className="sticky top-0 bg-card/95 backdrop-blur-lg border-b border-border z-30 px-4 py-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-center">Edit Profile</h1>
          <div className="flex items-center gap-2">
            <LanguageSwitcher position="top-right" className="relative" />
            <NotificationBell />
          </div>
        </div>
      </header>

      <div className="p-4 max-w-lg mx-auto">
        {/* Main Profile Photo */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center overflow-hidden ring-4 ring-pop/20">
              {profile?.photo_url ? (
                <img src={profile.photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl font-bold text-muted-foreground">{name?.[0] || "?"}</span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePhotoChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-pop text-white flex items-center justify-center shadow-button disabled:opacity-50"
            >
              <Camera className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="bg-card rounded-2xl p-4 mb-4">
          <h3 className="font-semibold mb-3">Your Photos</h3>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo, index) => {
              const isMain = photo.is_primary || index === 0;
              return (
                <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden bg-muted group">
                  <img src={photo.url} alt="Photo" className="w-full h-full object-cover" />
                  
                  {/* Main Badge */}
                  {isMain && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-pop text-white text-xs font-semibold rounded flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      Main
                    </div>
                  )}
                  
                  {/* Action Buttons - Only in Edit Mode */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {!isMain && (
                      <button
                        onClick={() => handleSetMainPhoto(photo.id)}
                        className="p-1.5 rounded-full bg-white/90 hover:bg-white text-foreground transition-colors"
                        title="Set as main"
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const input = replaceInputRefs.current[photo.id];
                        if (input) input.click();
                      }}
                      disabled={uploading || replacingPhotoId === photo.id}
                      className="p-1.5 rounded-full bg-white/90 hover:bg-white text-foreground transition-colors disabled:opacity-50"
                      title="Replace photo"
                    >
                      {replacingPhotoId === photo.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(photo)}
                      className="p-1.5 rounded-full bg-destructive text-white hover:bg-destructive/90 transition-colors"
                      title="Delete photo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Hidden replace input */}
                  <input
                    ref={(el) => (replaceInputRefs.current[photo.id] = el)}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleReplacePhoto(photo.id, file);
                      }
                      e.target.value = "";
                    }}
                    className="hidden"
                  />
                </div>
              );
            })}
            
            {/* Add Photo Slots */}
            {Array.from({ length: 6 - photos.length }).map((_, idx) => (
              <button
                key={`add-${idx}`}
                onClick={() => {
                  photoInputRef.current?.click();
                }}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-border flex items-center justify-center hover:border-pop hover:bg-pop/5 transition-colors disabled:opacity-50"
              >
                <Plus className="w-6 h-6 text-muted-foreground" />
              </button>
            ))}
          </div>
          {/* Single hidden input for adding photos */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={handleAddPhoto}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground mt-2">Add up to 6 photos. The photo marked 'Main' is your profile picture.</p>
        </div>

        {/* Profile Video Section - Original single video */}
        <ProfileVideoSection />

        {/* Multiple Videos Manager with pre-match visibility */}
        <ProfileVideosManager />

        {/* Profile Form */}
        <div className="space-y-4 bg-card rounded-2xl p-6">
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Age</label>
            <input type="number" value={age} onChange={(e) => setAge(e.target.value ? parseInt(e.target.value) : "")} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder="Your age" min={18} max={120} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Gender</label>
            <select value={gender} onChange={(e) => setGender(e.target.value as GenderType)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non_binary">Non-binary</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none resize-none h-24" placeholder="Tell us about yourself..." maxLength={120} />
            <p className="text-xs text-muted-foreground text-right">{bio.length}/120</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder="City, Country" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("profile.religion")}</label>
            <input value={religion} onChange={(e) => setReligion(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder={t("profile.religion")} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("profile.profession")}</label>
            <input value={profession} onChange={(e) => setProfession(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder={t("profile.profession")} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("profile.annualIncome")}</label>
            <select value={annualIncome} onChange={(e) => setAnnualIncome(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
              <option value="">{t("profile.selectIncomeRange")}</option>
              <option value="<$25k">Less than $25,000</option>
              <option value="$25-50k">$25,000 - $50,000</option>
              <option value="$50-75k">$50,000 - $75,000</option>
              <option value="$75-100k">$75,000 - $100,000</option>
              <option value="$100-150k">$100,000 - $150,000</option>
              <option value="$150-250k">$150,000 - $250,000</option>
              <option value="$250k+">$250,000+</option>
            </select>
          </div>
          
          {/* Personal Information Section */}
          <div className="pt-4 border-t border-border">
            <h3 className="text-lg font-semibold mb-4">{t("profile.personalInformation")}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.country")}</label>
                <input value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder={t("profile.country")} />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.nationality")}</label>
                <input value={nationality} onChange={(e) => setNationality(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder={t("profile.nationality")} />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.maritalStatus")}</label>
                <select value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
                  <option value="">{t("profile.selectMaritalStatus")}</option>
                  <option value="single">{t("profile.single")}</option>
                  <option value="divorced">{t("profile.divorced")}</option>
                  <option value="widowed">{t("profile.widowed")}</option>
                  <option value="separated">{t("profile.separated")}</option>
                  <option value="prefer_not_to_say">{t("profile.preferNotToSay")}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.education")}</label>
                <input value={education} onChange={(e) => setEducation(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none" placeholder={t("profile.education")} />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.socioeconomicStatus")}</label>
                <select value={socioeconomicStatus} onChange={(e) => setSocioeconomicStatus(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
                  <option value="">{t("profile.selectSocioeconomicStatus")}</option>
                  <option value="working_class">{t("profile.workingClass")}</option>
                  <option value="lower_middle_class">{t("profile.lowerMiddleClass")}</option>
                  <option value="middle_class">{t("profile.middleClass")}</option>
                  <option value="upper_middle_class">{t("profile.upperMiddleClass")}</option>
                  <option value="upper_class">{t("profile.upperClass")}</option>
                  <option value="prefer_not_to_say">{t("profile.preferNotToSay")}</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Lifestyle & Intent Section */}
          <div className="pt-4 border-t border-border">
            <h3 className="text-lg font-semibold mb-4">{t("profile.lifestyleAndIntent")}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.lookingFor")}</label>
                <select value={lookingFor} onChange={(e) => setLookingFor(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
                  <option value="">{t("profile.selectLookingFor")}</option>
                  <option value="marriage">{t("profile.marriage")}</option>
                  <option value="serious_relationship">{t("profile.seriousRelationship")}</option>
                  <option value="friendship">{t("profile.friendship")}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.willingToRelocate")}</label>
                <select value={willingToRelocate} onChange={(e) => setWillingToRelocate(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
                  <option value="">{t("profile.selectWillingToRelocate")}</option>
                  <option value="yes">{t("profile.yes")}</option>
                  <option value="no">{t("profile.no")}</option>
                  <option value="maybe">{t("profile.maybe")}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t("profile.children")}</label>
                <select value={children} onChange={(e) => setChildren(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none">
                  <option value="">{t("profile.selectChildren")}</option>
                  <option value="have_children">{t("profile.haveChildren")}</option>
                  <option value="want_children">{t("profile.wantChildren")}</option>
                  <option value="do_not_want">{t("profile.doNotWant")}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Interests Section */}
        <div className="mt-4">
          <InterestsButton onClick={() => setShowInterests(true)} />
        </div>

        <button onClick={handleSave} disabled={loading} className="w-full mt-6 py-4 rounded-xl pop-gradient text-white font-semibold shadow-button flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50">
          <Save className="w-5 h-5" />
          {loading ? "Saving..." : "Save Profile"}
        </button>
      </div>

      {/* Interests Modal */}
      <InterestSelectorModal
        isOpen={showInterests}
        onClose={() => setShowInterests(false)}
      />

      {/* Delete Photo Confirmation Dialog */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this photo from your profile. You can always add a new one later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showDeleteConfirm && handleDeletePhoto(showDeleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
